import { useState } from 'react';
import { BlockRenderer, type CmsBlock } from './BlockRenderer';
import { useEditMode } from '../../contexts/EditModeContext';

interface TabDef {
  label: string;
}

interface TabsProps {
  style?: 'underline' | 'pills' | 'boxed';
  tabs?: TabDef[];
}

/**
 * TabsBlock — container block like Row/Columns.
 * Each tab in `props.tabs` defines a panel.
 * Child blocks are assigned to tabs via `props._col` (same pattern as Row).
 */
export function TabsBlock({ block }: { block: CmsBlock }) {
  const { isEditMode } = useEditMode();
  const { style = 'underline', tabs: tabDefs = [] } = (block.props ?? {}) as TabsProps;
  const children = block.children ?? [];
  const [activeTab, setActiveTab] = useState(0);

  // Group children by _col (same pattern as RowBlock)
  const panels = tabDefs.map((def, i) => ({
    label: def.label || `Tab ${i + 1}`,
    blocks: children
      .filter((c) => ((c.props._col as number) ?? 0) === i)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }));

  if (panels.length === 0 && !isEditMode) return null;

  const tabStyles = {
    underline: {
      list: 'border-b border-zinc-200 flex gap-0',
      active: 'border-b-2 border-indigo-500 text-indigo-600 px-4 py-2 text-sm font-medium',
      inactive: 'border-b-2 border-transparent text-zinc-500 hover:text-zinc-700 px-4 py-2 text-sm',
    },
    pills: {
      list: 'flex gap-1 bg-zinc-100 p-1 rounded-lg',
      active: 'bg-white shadow-sm rounded-md px-4 py-1.5 text-sm font-medium text-zinc-900',
      inactive: 'px-4 py-1.5 text-sm text-zinc-500 hover:text-zinc-700 rounded-md',
    },
    boxed: {
      list: 'flex gap-0 border border-zinc-200 rounded-lg overflow-hidden',
      active: 'bg-indigo-50 text-indigo-700 px-4 py-2 text-sm font-medium border-r border-zinc-200 last:border-r-0',
      inactive: 'bg-white text-zinc-500 hover:bg-zinc-50 px-4 py-2 text-sm border-r border-zinc-200 last:border-r-0',
    },
  };

  const s = tabStyles[style] ?? tabStyles.underline;

  return (
    <div>
      <div className={s.list} role="tablist">
        {panels.map((panel, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === activeTab}
            onClick={() => setActiveTab(i)}
            className={i === activeTab ? s.active : s.inactive}
          >
            {panel.label}
          </button>
        ))}
      </div>
      <div className="pt-4" role="tabpanel">
        {panels[activeTab] && (
          <>
            {panels[activeTab].blocks.length > 0 && (
              <div className="space-y-4">
                {panels[activeTab].blocks.map((child) => (
                  <BlockRenderer key={child.id} block={child} />
                ))}
              </div>
            )}
            {isEditMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.parent.postMessage({
                    type: 'ADD_BLOCK_TO_COLUMN',
                    parentBlockId: block.id,
                    column: activeTab,
                  }, '*');
                }}
                className={`w-full border-2 border-dashed border-zinc-300 hover:border-indigo-400 rounded-lg p-3 text-center text-zinc-400 hover:text-indigo-500 text-xs transition-colors cursor-pointer ${panels[activeTab].blocks.length > 0 ? 'mt-3' : ''}`}
              >
                + Add to "{panels[activeTab].label}"
              </button>
            )}
          </>
        )}
      </div>
      {isEditMode && panels.length === 0 && (
        <div className="border-2 border-dashed border-indigo-300 bg-indigo-50 rounded-lg px-4 py-6 text-center">
          <div className="text-sm font-semibold text-indigo-500">Tabs</div>
          <div className="text-xs text-indigo-400 mt-1">Add tabs in the config panel first</div>
        </div>
      )}
    </div>
  );
}
